import React from "react";
import { Box, Flex, Heading } from "@chakra-ui/react";
import { format } from "date-fns";
import { Table } from "components";
import { useCurrentCase, useOrganizationSettings } from "duck";
import { FluidTemperature } from "requests";
import { Create } from "./components";
import { hooks, utils } from "./duck";

interface FluidsAndTemperatureProps {
  timeIntervals: string[];
}

const FluidsAndTemperature: React.FC<FluidsAndTemperatureProps> = ({
  timeIntervals
}) => {
  const {
    patientId,
    fluidTemperatures,
    createdAt,
    procedures,
    fluidType
  } = useCurrentCase(medicalCase => ({
    patientId: medicalCase.data.client.patient._id,
    fluidTemperatures: medicalCase.data.client?.patient?.fluidTemperatures,
    createdAt: medicalCase.data.createdAt,
    procedures: medicalCase.data.client?.patient?.patientProcedures,
    fluidType: medicalCase.data.client.patient.miscellaneousGroup.fluidType
  }));
  const { temperatureUnitSymbol } = useOrganizationSettings();
  const caseCreationDate = new Date(createdAt);
  const handleBlur = hooks.useUpdateFluidAndTemperature(caseCreationDate);
  const isAnesthesiaStarted = !!procedures?.find(
    procedure => procedure.name === "Anesthesia"
  )?.startTime;

  const tableData = utils.generateFluidAndTemperatureData(
    fluidTemperatures as FluidTemperature[],
    temperatureUnitSymbol,
    fluidType
  );

  const disabledIntervals =
    fluidTemperatures?.map(item => item.timeMonitoring) || [];

  return (
    <Box>
      <Flex justifyContent="space-between" alignItems="center" mb={4}>
        <Heading as="h3" color="black" fontSize="lg" lineHeight="7">
          Fluids and temperature
        </Heading>
        <Create
          patientId={patientId}
          timeIntervals={timeIntervals}
          disabledIntervals={disabledIntervals}
          caseCreationDate={caseCreationDate}
        />
      </Flex>
      <Table
        data={tableData}
        editable
        scrollToCurrentTime={isAnesthesiaStarted}
      >
        {[
          <Table.Column
            dataKey="name"
            key="name"
            label="Time"
            render={({ value: name }) => {
              return name;
            }}
            sticky
          />,
          ...timeIntervals.map(time => {
            return (
              <Table.Column
                dataKey="val"
                key={time}
                label={(time as unknown) as string}
                render={({ value, record, isEditable }) => {
                  const curTimeValue = value?.find((el: any) => {
                    const formattedTime = format(
                      new Date(el.timeMonitoring),
                      "h:mm a"
                    );
                    return formattedTime === time;
                  });

                  return isEditable &&
                    disabledIntervals.includes(curTimeValue?.timeMonitoring) ? (
                    <Table.NumberInput
                      defaultValue={curTimeValue?.value}
                      onBlur={newValue =>
                        handleBlur(newValue, curTimeValue, record.name, time)
                      }
                    />
                  ) : (
                    curTimeValue?.value
                  );
                }}
              />
            );
          })
        ]}
      </Table>
    </Box>
  );
};

export default FluidsAndTemperature;
